import { useAPI } from "@/contexts/APIContext";
import {
  formatDuration,
  formatTimeSlot,
  generatePageNumbers,
  getAppointmentStatus,
  groupByDate,
} from "@/utils/appointment";
import usePagination from "@/utils/usePagination";
import { format } from "date-fns";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { User } from "snudbud-backbone-sdk";

interface AvailabilityScheduleProps {
  userId: string;
  itemsPerPage?: number;
  isOwnProfile?: boolean;
}

export default function AvailabilitySchedule({
  userId,
  itemsPerPage = 10,
  isOwnProfile = false,
}: AvailabilityScheduleProps) {
  const { api } = useAPI();

  // Use the enhanced usePagination hook
  const scheduleSlotsPagination = usePagination<User.Schedule.QueryReturn["slots"][0]>({
    fn: async ({ offset, limit }) => {
      if (!userId) return { count: 0, items: [] };
      const start = new Date(new Date().getTime() - 1000 * 60 * 60 * 2);

      // If viewing own profile, use queryOwn to get both available and booked slots
      if (isOwnProfile) {
        const result = await api.user.schedule.queryOwn({
          start,
          offset,
          limit: limit || itemsPerPage,
        });
        return { count: result.count, items: result.slots };
      } else {
        // For other profiles, use regular query to get only available slots
        const result = await api.user.schedule.query({
          userId,
          start,
          offset,
          limit: limit || itemsPerPage,
        });
        return { count: result.count, items: result.slots };
      }
    },
    initial: false,
    itemsPerPage,
  });

  useEffect(() => {
    scheduleSlotsPagination.loadPage(1);
  }, [userId]);

  // Group slots by date using our utility function
  const groupedSlots = groupByDate(scheduleSlotsPagination.data);

  const hasAvailability = scheduleSlotsPagination.data.length > 0;
  const totalPages = Math.ceil(scheduleSlotsPagination.count / itemsPerPage);
  const pageNumbers = generatePageNumbers(scheduleSlotsPagination.currentPage, totalPages);

  // Handle page change
  const handlePageChange = (page: number) => {
    scheduleSlotsPagination.loadPage(page);
  };

  // Function to render slot content based on booking status
  const renderSlot = (slot: User.Schedule.QueryReturn["slots"][0] | User.Schedule.QueryOwnReturn["slots"][0]) => {
    const isBooked = "bookedByUser" in slot && slot.bookedByUser;
    const status = getAppointmentStatus(slot.startsAt, slot.duration);
    const isInSession = status === "in-session";

    const statusLabel = isInSession ? (
      <span className="ml-2 text-xs py-1 px-2 bg-green-100 text-green-700 rounded">In Session</span>
    ) : null;

    return (
      <div
        key={slot.scheduleSlotId}
        className={`block w-full py-3 px-4 rounded-lg text-sm border ${
          isInSession
            ? "bg-green-50/30 border-green-200"
            : isBooked
            ? "bg-blue-50 border-blue-200"
            : "bg-white border-gray-200 hover:border-blue-500 hover:bg-blue-500/5"
        } ${!isBooked && !isInSession ? "group" : ""} transition-all`}
      >
        <div className="flex justify-between items-center">
          <span
            className={`font-medium ${
              isInSession ? "text-green-700" : isBooked ? "text-blue-700" : "text-gray-800 group-hover:text-blue-500"
            }`}
          >
            {formatTimeSlot(slot.startsAt, slot.duration)}
            {statusLabel}
          </span>
          <span
            className={`text-xs py-1 px-2 rounded ${
              isInSession
                ? "bg-green-100 text-green-700"
                : isBooked
                ? "bg-blue-100 text-blue-800"
                : "bg-gray-100 group-hover:bg-blue-500/10"
            }`}
          >
            {formatDuration(slot.duration)}
          </span>
        </div>

        {isBooked && "bookedByUser" in slot && slot.bookedByUser && (
          <div className="flex items-center mt-2 text-sm">
            <div className="flex-shrink-0 mr-2">
              <img
                src={slot.bookedByUser.profileImageUrl || "https://via.placeholder.com/24"}
                alt={slot.bookedByUser.name}
                className="w-5 h-5 rounded-full"
              />
            </div>
            <span className="text-blue-700">
              Booked by{" "}
              <Link to={`/profile/${slot.bookedByUser.userId}`} className="font-semibold hover:underline">
                {slot.bookedByUser.name}
              </Link>
            </span>
            {isInSession && (
              <Link
                to={`/chat/${slot.bookedByUser?.userId}`}
                className="ml-auto text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
              >
                Join Session
              </Link>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="mt-8">
      <h2 className="text-xl font-semibold mb-4">Availability</h2>
      <div className="bg-white rounded-2xl p-6 shadow">
        {scheduleSlotsPagination.isLoading && scheduleSlotsPagination.data.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="animate-pulse">Loading availability...</div>
          </div>
        ) : hasAvailability ? (
          <div className="space-y-6">
            {groupedSlots.map(([dateStr, slots]) => (
              <div key={dateStr} className="mb-4">
                <h3 className="font-medium text-lg text-gray-800 mb-3">
                  {format(new Date(dateStr), "EEEE, MMMM d, yyyy")}
                </h3>
                <div className="space-y-2">
                  {slots.map((slot) => {
                    const status = getAppointmentStatus(slot.startsAt, slot.duration);
                    const isBooked = "bookedByUser" in slot && slot.bookedByUser;

                    if (isBooked || status === "in-session") {
                      return renderSlot(slot);
                    }

                    return (
                      <Link
                        key={slot.scheduleSlotId}
                        to={`/booking/${userId}/${slot.scheduleSlotId}`}
                        className="group block w-full py-3 px-4 rounded-lg text-sm bg-white border border-gray-200 hover:border-blue-500 hover:bg-blue-500/5 cursor-pointer transition-all"
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-800 group-hover:text-blue-500">
                            {formatTimeSlot(slot.startsAt, slot.duration)}
                          </span>
                          <span className="text-xs bg-gray-100 py-1 px-2 rounded group-hover:bg-blue-500/10">
                            {formatDuration(slot.duration)}
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}

            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center">
                <nav className="flex items-center space-x-1" aria-label="Pagination">
                  {/* Previous page button */}
                  <button
                    onClick={() => handlePageChange(scheduleSlotsPagination.currentPage - 1)}
                    disabled={scheduleSlotsPagination.currentPage === 1 || scheduleSlotsPagination.isLoading}
                    className={`p-2 rounded-full ${
                      scheduleSlotsPagination.currentPage === 1 || scheduleSlotsPagination.isLoading
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                    } transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50`}
                    aria-label="Previous page"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                    </svg>
                  </button>

                  {/* Page numbers */}
                  {pageNumbers.map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={scheduleSlotsPagination.isLoading}
                      className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${
                        pageNum === scheduleSlotsPagination.currentPage
                          ? "bg-blue-500 text-white"
                          : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                      }`}
                      aria-current={pageNum === scheduleSlotsPagination.currentPage ? "page" : undefined}
                    >
                      {pageNum}
                    </button>
                  ))}

                  {/* Next page button */}
                  <button
                    onClick={() => handlePageChange(scheduleSlotsPagination.currentPage + 1)}
                    disabled={scheduleSlotsPagination.currentPage === totalPages || scheduleSlotsPagination.isLoading}
                    className={`p-2 rounded-full ${
                      scheduleSlotsPagination.currentPage === totalPages || scheduleSlotsPagination.isLoading
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                    } transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50`}
                    aria-label="Next page"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                    </svg>
                  </button>
                </nav>
              </div>
            )}

            {scheduleSlotsPagination.isLoading && (
              <div className="mt-4 text-center py-2">
                <div className="inline-flex items-center text-gray-500">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Loading...
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <svg
              className="w-16 h-16 mx-auto mb-4 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              ></path>
            </svg>
            <p className="mb-2 font-medium">No availability has been set up</p>
            <p className="text-sm">Check back later or try selecting a different date range.</p>
          </div>
        )}
      </div>
    </section>
  );
}
